import DeleteIcon from "@mui/icons-material/Delete";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { SortableHeaderCell } from "components/common/SortableHeaderCell";
import { useNotifyStatus } from "contexts/status";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useTableSort, type SortValueGetters } from "hooks/useTableSort";
import { useCallback, useState } from "react";
import {
  useCreateLocationSupportAssociationMutation,
  useDeleteLocationSupportAssociationMutation,
  useListInstallationLocationsQuery,
  useListLocationSupportAssociationsQuery,
  type ScrapEyeLocationSupportAssociationRead,
} from "src/store/api/generatedApi";

type SupportAssociationColumn = "labellingLocation" | "supportingLocation";

const supportAssociationSortValueGetters: SortValueGetters<
  ScrapEyeLocationSupportAssociationRead,
  SupportAssociationColumn
> = {
  labellingLocation: (association) => association.labelling_location_name,
  supportingLocation: (association) => association.supporting_location_name,
};

export function SupportingLocationAssociationSection() {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const notifyStatus = useNotifyStatus();

  const [labellingLocation, setLabellingLocation] = useState("");
  const [supportingLocation, setSupportingLocation] = useState("");

  const { data: locations } = useListInstallationLocationsQuery({ tenantName });
  const { data: associations, isLoading } =
    useListLocationSupportAssociationsQuery({ tenantName });

  const {
    sortedRows: sortedAssociations,
    sort: associationSort,
    toggleSort: toggleAssociationSort,
  } = useTableSort({
    rows: associations,
    sortValueGetters: supportAssociationSortValueGetters,
    initialSort: null,
  });

  const [createAssociation] = useCreateLocationSupportAssociationMutation();
  const [deleteAssociation] = useDeleteLocationSupportAssociationMutation();

  const labellingLocations = locations?.filter(
    (loc) => loc.location_type === "labelling" || !loc.location_type
  );
  const supportingLocations = locations?.filter(
    (loc) => loc.location_type === "supporting"
  );

  const handleCreate = useCallback(async () => {
    if (!labellingLocation || !supportingLocation) {
      notifyStatus({
        text: t("supportAssociationCreateError"),
        type: "error",
      });
      return;
    }

    try {
      await createAssociation({
        tenantName,
        scrapEyeLocationSupportAssociationCreate: {
          labelling_location_name: labellingLocation,
          supporting_location_name: supportingLocation,
        },
      }).unwrap();

      notifyStatus({
        text: t("supportAssociationCreateSuccess"),
        type: "success",
      });
      setLabellingLocation("");
      setSupportingLocation("");
    } catch {
      notifyStatus({
        text: t("supportAssociationCreateError"),
        type: "error",
      });
    }
  }, [
    labellingLocation,
    supportingLocation,
    createAssociation,
    tenantName,
    notifyStatus,
    t,
  ]);

  const handleDelete = useCallback(
    async (associationId: number) => {
      try {
        await deleteAssociation({ tenantName, associationId }).unwrap();
        notifyStatus({
          text: t("supportAssociationDeleteSuccess"),
          type: "success",
        });
      } catch {
        notifyStatus({
          text: t("supportAssociationDeleteError"),
          type: "error",
        });
      }
    },
    [deleteAssociation, tenantName, notifyStatus, t]
  );

  return (
    <Card>
      <CardHeader title={t("supportAssociationsTitle")} />
      <CardContent>
        <Stack spacing={3}>
          <Stack direction="row" spacing={2}>
            <TextField
              select
              label={t("labellingLocation")}
              value={labellingLocation}
              onChange={(e) => {
                setLabellingLocation(e.target.value);
              }}
              size="small"
              sx={{ minWidth: 200 }}
            >
              {labellingLocations?.map((loc) => (
                <MenuItem key={loc.name} value={loc.name}>
                  {loc.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t("supportingLocation")}
              value={supportingLocation}
              onChange={(e) => {
                setSupportingLocation(e.target.value);
              }}
              size="small"
              sx={{ minWidth: 200 }}
            >
              {supportingLocations?.map((loc) => (
                <MenuItem key={loc.name} value={loc.name}>
                  {loc.name}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              onClick={() => void handleCreate()}
              disabled={!labellingLocation || !supportingLocation}
            >
              {t("create")}
            </Button>
          </Stack>

          {isLoading ? (
            <CircularProgress />
          ) : sortedAssociations && sortedAssociations.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <SortableHeaderCell
                    column="labellingLocation"
                    sort={associationSort}
                    onSort={toggleAssociationSort}
                  >
                    {t("labellingLocation")}
                  </SortableHeaderCell>
                  <SortableHeaderCell
                    column="supportingLocation"
                    sort={associationSort}
                    onSort={toggleAssociationSort}
                  >
                    {t("supportingLocation")}
                  </SortableHeaderCell>
                  <TableCell align="right">{t("actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedAssociations.map((assoc) => (
                  <TableRow key={assoc.id}>
                    <TableCell>{assoc.labelling_location_name}</TableCell>
                    <TableCell>{assoc.supporting_location_name}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => void handleDelete(assoc.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography color="text.secondary">
              {t("supportAssociationsEmpty")}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
